import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Typography from '@mui/material/Typography';
import { NavButton, railItemSx, railLabelSx } from './nav-button';
import { navHeight, sidebarCollapsed, sidebarExpanded } from './constants';
import { isNavItemActive } from './nav-match';
import type { AppShellProps, NavItem, SidebarStatus } from './types';

export type SidebarProps = {
  navItems: NavItem[];
  currentPath: string;
  linkComponent: AppShellProps['linkComponent'];
  collapsed: boolean;
  emitDocAttributes: boolean;
  secondaryNavItems?: NavItem[];
  sidebarStatus?: SidebarStatus;
  stickyTop: number;
};

const statusToneColors: Record<SidebarStatus['tone'], string> = {
  success: 'success.main',
  warning: 'warning.main',
  error: 'error.main',
  neutral: 'text.disabled',
};

type StatusRowProps = {
  status: SidebarStatus;
  collapsed: boolean;
  linkComponent: AppShellProps['linkComponent'];
};

function StatusRow({ status, collapsed, linkComponent }: StatusRowProps) {
  const dotColor = statusToneColors[status.tone];
  const dot = (
    <Box
      aria-hidden="true"
      sx={{ width: 14, height: 14, borderRadius: '50%', flexShrink: 0, bgcolor: dotColor }}
    />
  );
  const label = <Typography sx={railLabelSx(collapsed, false)}>{status.label}</Typography>;
  let row;
  if (status.href) {
    const linkProps = status.spa
      ? { component: linkComponent, to: status.href }
      : { component: 'a' as const, href: status.href };
    row = (
      <ButtonBase
        {...linkProps}
        onClick={status.onClick}
        title={status.label}
        sx={{ ...railItemSx(collapsed, false), color: 'text.secondary' }}
      >
        {dot}
        {label}
      </ButtonBase>
    );
  } else {
    row = (
      <Box
        title={status.label}
        aria-label={status.label}
        sx={{ ...railItemSx(collapsed, false), color: 'text.secondary' }}
      >
        {dot}
        {label}
      </Box>
    );
  }
  return row;
}

export function Sidebar({
  navItems,
  currentPath,
  linkComponent,
  collapsed,
  emitDocAttributes,
  secondaryNavItems,
  sidebarStatus,
  stickyTop,
}: SidebarProps) {
  const sidebarWidth = collapsed ? sidebarCollapsed : sidebarExpanded;
  const hasFooter = Boolean(sidebarStatus) || (secondaryNavItems ?? []).length > 0;

  return (
    <Box
      component="nav"
      aria-label="Primary"
      sx={{
        width: sidebarWidth,
        flexShrink: 0,
        position: 'sticky',
        top: `${stickyTop}px`,
        alignSelf: 'flex-start',
        minHeight: hasFooter ? `calc(100vh - ${navHeight}px)` : undefined,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        py: 1,
        transition: 'width 0.2s ease',
        '@media (prefers-reduced-motion: reduce)': {
          transition: 'none',
        },
      }}
    >
      {navItems.map((item) => (
        <NavButton
          key={item.to}
          item={item}
          active={isNavItemActive(item, currentPath)}
          collapsed={collapsed}
          linkComponent={linkComponent}
          emitDocAttributes={emitDocAttributes}
        />
      ))}
      {hasFooter ? (
        <Box sx={{ mt: 'auto', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {sidebarStatus ? (
            <StatusRow
              status={sidebarStatus}
              collapsed={collapsed}
              linkComponent={linkComponent}
                />
          ) : null}
          {(secondaryNavItems ?? []).map((item) => (
            <NavButton
              key={item.to}
              item={item}
              active={isNavItemActive(item, currentPath)}
              collapsed={collapsed}
              linkComponent={linkComponent}
              emitDocAttributes={emitDocAttributes}
                />
          ))}
        </Box>
      ) : null}
    </Box>
  );
}
